/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, ArrowRight, Package, Sparkles, Compass } from "lucide-react";
import SmartSearch from "@/components/SmartSearch";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [themeTours, setThemeTours] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [filteredThemeTours, setFilteredThemeTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchApplied, setSearchApplied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch both packages and theme tours
        const [packagesRes, themesToursRes] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/theme-tours`)
        ]);
        
        setPackages(packagesRes.data);
        setThemeTours(themesToursRes.data);
        
        // Check for URL params and apply filters if needed
        const category = searchParams.get("category");
        const type = searchParams.get("type");
        const destination = searchParams.get("destination");
        const duration = searchParams.get("duration");
        const maxPrice = searchParams.get("max_price");
        const search = searchParams.get("search");
        const theme = searchParams.get("theme");
        
        // If any filters in URL, apply client-side filtering
        if (category || type || destination || duration || maxPrice || search || theme) {
          let filteredPkgs = packagesRes.data;
          let filteredThemes = themesToursRes.data;
          
          if (category) {
            filteredPkgs = filteredPkgs.filter(p => p.category === category);
            filteredThemes = filteredThemes.filter(t => t.category === category);
          }
          if (type) {
            filteredPkgs = filteredPkgs.filter(p => p.type === type || (p.types && p.types.includes(type)));
          }
          if (theme) {
            filteredThemes = filteredThemes.filter(t => t.theme === theme);
          }
          if (destination) {
            filteredPkgs = filteredPkgs.filter(p => 
              p.destination?.toLowerCase().includes(destination.toLowerCase()) ||
              p.country?.toLowerCase().includes(destination.toLowerCase())
            );
            filteredThemes = filteredThemes.filter(t => 
              t.destination?.toLowerCase().includes(destination.toLowerCase()) ||
              t.country?.toLowerCase().includes(destination.toLowerCase())
            );
          }
          
          setFilteredPackages(filteredPkgs);
          setFilteredThemeTours(filteredThemes);
          setSearchApplied(true);
          setActiveFilters({ category, type, destination, theme, duration: duration ? parseInt(duration) : null });
        } else {
          setFilteredPackages(packagesRes.data);
          setFilteredThemeTours(themesToursRes.data);
        }
      } catch (e) {
        logger.error("Error fetching data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  // Handle search from SmartSearch component
  const handleSearch = (results, filters, themeTourResults = []) => {
    setFilteredPackages(results);
    setFilteredThemeTours(themeTourResults);
    setActiveFilters(filters);
    setSearchApplied(Object.values(filters).some(v => v && (Array.isArray(v) ? v[1] < 500000 : true)));
    
    // Update URL params
    const newParams = new URLSearchParams();
    if (filters.category) newParams.set("category", filters.category);
    if (filters.type) newParams.set("type", filters.type);
    if (filters.theme) newParams.set("theme", filters.theme);
    if (filters.country) newParams.set("destination", filters.country);
    if (filters.destination) newParams.set("destination", filters.destination);
    setSearchParams(newParams);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const displayPackages = searchApplied ? filteredPackages : packages;
  const displayThemeTours = searchApplied ? filteredThemeTours : [];

  return (
    <div data-testid="packages-page">
      {/* Hero with Smart Search */}
      <section className="relative pt-32 pb-24 bg-slate-900" data-testid="packages-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1528181304800-259b08848526?w=1920&q=80"
            alt="Tour Packages"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption text-coral mb-4 block flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" /> Smart Search
            </span>
            <h1 className="heading-1 text-white mb-6">Find Your Perfect Trip</h1>
            <p className="text-lg text-white/80 mb-8">
              Search by destination, duration, budget, or trip type. Our smart search helps you find exactly what you're looking for.
            </p>
            
            {/* Smart Search Component */}
            <SmartSearch 
              packages={packages}
              themeTours={themeTours}
              onSearch={handleSearch}
              className="max-w-3xl mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Results Header */}
      <section className="py-6 bg-accent border-b border-slate-200">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-slate-400" />
              <span className="text-slate-600">
                {loading ? 'Loading...' : searchApplied ? 'Showing filtered results' : 'Browse our travel packages'}
              </span>
            </div>
            
            {searchApplied && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilteredPackages(packages);
                  setFilteredThemeTours(themeTours);
                  setActiveFilters({});
                  setSearchApplied(false);
                  setSearchParams({});
                }}
                className="text-coral hover:text-coral/80"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="section-padding bg-white" data-testid="packages-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200 rounded-t-xl" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-6 bg-slate-200 rounded" />
                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayPackages.length === 0 ? (
            <div className="text-center py-16">
              <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No packages found</h3>
              <p className="text-slate-600 mb-6">
                Try adjusting your search or filters to find what you're looking for.
              </p>
              <Button 
                onClick={() => {
                  setFilteredPackages(packages);
                  setActiveFilters({});
                  setSearchApplied(false);
                  setSearchParams({});
                }}
                className="bg-coral hover:bg-coral/90"
              >
                View All Packages
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.1, 0.5) }}
                >
                  <Card className="group h-full flex flex-col overflow-hidden hover:shadow-xl transition-shadow duration-300" data-testid={`package-${pkg.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={pkg.image}
                        alt={pkg.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        <Badge className={`${pkg.category === 'international' ? 'bg-blue-500' : 'bg-green-500'} text-white`}>
                          {pkg.category === 'international' ? '✈️' : '🇮🇳'} {pkg.category}
                        </Badge>
                        {pkg.type && (
                          <Badge variant="secondary" className="bg-white/90 text-slate-700">
                            {pkg.type}
                          </Badge>
                        )}
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-4 text-white/90 text-sm">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {pkg.country || pkg.destination}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {pkg.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6 flex flex-col flex-1">
                      <h3 className="text-xl font-heading font-semibold text-primary mb-3 line-clamp-2 group-hover:text-coral transition-colors">
                        {pkg.title}
                      </h3>
                      {pkg.city && (
                        <p className="text-sm text-slate-500 mb-3 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {pkg.city}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {pkg.highlights?.slice(0, 3).map((h) => (
                          <span
                            key={h}
                            className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                          >
                            {h}
                          </span>
                        ))}
                        {pkg.highlights?.length > 3 && (
                          <span className="px-2 py-1 bg-slate-100 text-slate-400 text-xs rounded-full">
                            +{pkg.highlights.length - 3} more
                          </span>
                        )}
                      </div>
                      <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                        <div>
                          <span className="text-xs text-slate-500 uppercase tracking-wide">Starting from</span>
                          <div className="text-2xl font-bold text-coral">
                            ₹{pkg.price?.toLocaleString()}
                          </div>
                        </div>
                        <Link to={`/packages/${pkg.id}`}>
                          <Button
                            className="bg-primary hover:bg-primary/90 text-white rounded-full"
                            data-testid={`view-pkg-${pkg.id}`}
                          >
                            View
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-coral to-coral/80" data-testid="packages-cta">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <h2 className="heading-2 text-white mb-6">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Our travel experts can create a personalized itinerary tailored to your preferences, 
              budget, and travel style. Let us plan your dream trip!
            </p>
            <Link to="/get-quote">
              <Button className="bg-white text-coral hover:bg-white/90 h-14 px-10 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transition-all" data-testid="custom-package-cta">
                Get Custom Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PackagesPage;
